import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import AuthGuard from '../components/AuthGuard';
import CardFundPromotionModal from '../components/CardFundPromotionModal';
import CardFundPromotionModalV2 from '../components/CardFundPromotionModalV2';
import CardFundPromotionModalV3 from '../components/CardFundPromotionModalV3';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

const fundTypes = [
  { key: 'gongshang', image: '/images/jijin_gongsnag.png' },
  { key: 'jianshe', image: '/images/jijin_jianshe.png' },
  { key: 'nongye', image: '/images/jijin_nongey.png' },
  { key: 'zhongyin', image: '/images/jijin_gongyin.png' },
];

interface ReceiptItem {
  id: number;
  fund_type: string;
  cert_number: string;
  created_at: string;
  fund_info: { name: string; value: number } | null;
}

interface LimitedFundData {
  start_date: string | null;
  end_date: string | null;
  max_count: number;
  purchased_count: number;
  price: number;
  is_v2: boolean;
  is_v3: boolean;
}

export default function Fund() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [confirmFund, setConfirmFund] = useState<string | null>(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [certs, setCerts] = useState<ReceiptItem[]>([]);
  const [certsLoading, setCertsLoading] = useState(false);

  const [showFundPromoModal, setShowFundPromoModal] = useState((import.meta as any).env?.VITE_CARD_PASSWORD_SETUP_ENABLED === 'true');
  const [showLimitedFund, setShowLimitedFund] = useState(false);
  const [limitedFundData, setLimitedFundData] = useState<LimitedFundData | null>(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [confirmLimitedPurchase, setConfirmLimitedPurchase] = useState(false);
  const [purchasingLimited, setPurchasingLimited] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (searchParams.get('status') === 'pending') {
      setShowPendingModal(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  function parseGmt8(dateStr: string) {
    const s = String(dateStr);
    return new Date(/Z|[+-]\d{2}:\d{2}/.test(s) ? s : s.replace(' ', 'T') + '+08:00');
  }

  useEffect(() => {
    api.get('/limited-fund/status').then(({ data }) => {
      setLimitedFundData(data);
      if (data.start_date && data.end_date) {
        const start = parseGmt8(data.start_date).getTime();
        const end = parseGmt8(data.end_date).getTime();
        const now = Date.now();
        if (now >= start && now <= end) {
          setShowLimitedFund(true);
        }
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (!limitedFundData?.end_date) return;

    const endTime = parseGmt8(limitedFundData.end_date).getTime();

    function tick() {
      const remaining = Math.max(0, endTime - Date.now());
      setCountdown({
        hours: Math.floor(remaining / (1000 * 60 * 60)),
        minutes: Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((remaining % (1000 * 60)) / 1000),
      });
      if (remaining <= 0 && countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    }

    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [limitedFundData?.end_date]);

  async function handlePurchase(fundType: string, product: string) {
    if (purchasing) return;
    setPurchasing(fundType);
    setConfirmFund(null);
    try {
      const { data } = await api.post('/fund-purchases', { fund_type: fundType, product });
      const payUrl = data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.success('订单已创建');
        setPurchasing(null);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '购买失败');
      setPurchasing(null);
    }
  }

  async function handleLimitedPurchase(product: string) {
    if (purchasingLimited) return;
    setPurchasingLimited(true);
    setConfirmLimitedPurchase(false);
    try {
      const { data } = await api.post('/limited-fund/purchase', { product });
      const payUrl = data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.success('订单已创建');
        setPurchasingLimited(false);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '购买失败');
      setPurchasingLimited(false);
    }
  }

  function openCertList() {
    setShowCertModal(true);
    setCertsLoading(true);
    api.get('/fund-purchases/receipts').then(({ data }) => {
      setCerts(data.purchases ?? []);
    }).catch(() => toast.error('加载失败')).finally(() => setCertsLoading(false));
  }

  const pad2 = (n: number) => String(n).padStart(2, '0');
  const isEventActive = limitedFundData?.start_date && (countdown.hours > 0 || countdown.minutes > 0 || countdown.seconds > 0);
  const canPurchaseLimited = isEventActive && limitedFundData && limitedFundData.purchased_count < limitedFundData.max_count;

  return (
    <div
      className="max-w-md mx-auto min-h-screen pb-6 pt-[260px] relative"
      style={{
        backgroundColor: '#5a0000',
        backgroundImage: 'url(/images/jijin_beijing.png)',
        backgroundPosition: 'top center',
        backgroundSize: '100% auto',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <AuthGuard />

      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>

      <div className="max-w-md">
        <button
          onClick={openCertList}
          className="block mx-auto text-[#ffd700] font-bold text-base tracking-wider"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)', textDecoration: 'underline', textUnderlineOffset: '4px' }}
        >
          查看我的证书
        </button>

        {isEventActive && (
          <button
            onClick={() => setShowLimitedFund(true)}
            className="block mx-auto mt-2 bg-gradient-to-r from-[#ffd700] to-[#ffaa00] text-[#8b0000] font-bold text-sm px-5 py-2 rounded-full animate-pulse"
            style={{ boxShadow: '0 0 15px rgba(255,215,0,0.5)' }}
          >
            限定基金抢购中
          </button>
        )}

        {(import.meta as any).env?.VITE_CARD_PASSWORD_SETUP_ENABLED === 'true' && (
          <button
            onClick={() => setShowFundPromoModal(true)}
            className="block mx-auto mt-2 bg-gradient-to-r from-[#ff4444] to-[#cc0000] text-white font-bold text-sm px-5 py-2 rounded-full animate-pulse"
            style={{ boxShadow: '0 0 15px rgba(255,68,68,0.5)' }}
          >
            红色初心·党员钱包基金
          </button>
        )}

        <div className="px-3 mt-4 space-y-4">
          {fundTypes.map((fund) => (
            <div key={fund.key}>
              <img src={fund.image} alt="" className="w-full rounded-lg" />
              <button
                onClick={() => setConfirmFund(fund.key)}
                disabled={purchasing === fund.key}
                className="block mx-auto mt-2 disabled:opacity-60"
              >
                <img
                  src="/images/jijin_liji.png"
                  alt="立即购买"
                  className="h-9 mx-auto"
                />
              </button>
            </div>
          ))}
        </div>

        <p
          className="text-center text-[#ffd700] text-xs mt-8 mb-4 tracking-[0.2em]"
          style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
        >
          党建引领 · 关爱党员 · 共享发展 · 共筑未来
        </p>
      </div>

      {showLimitedFund && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="relative w-[340px] max-h-[90vh] overflow-y-auto rounded-2xl" style={{ backgroundColor: '#3a0000' }}>
            <button
              onClick={() => setShowLimitedFund(false)}
              className="absolute top-2 right-2 z-20 w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>

            <div className="relative">
              <img src={limitedFundData?.is_v3 ? "/images/xianding_beijing_v3.png" : limitedFundData?.is_v2 ? "/images/xianding_beijing_v2.png" : "/images/xianding_beijing_v1.png"} alt="" className="w-full rounded-t-2xl" />

              <div className="absolute" style={{ bottom: '18.5%', left: 0, right: 0 }}>
                <div className="flex items-center justify-center gap-1.5">
                  <img src="/images/xianding_xiangou.png" alt="" className="w-5 h-5" />
                  <span className="text-[#ffecc0] text-sm font-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                    每人限购{limitedFundData?.max_count ?? 3}份：当前已购入{' '}
                    <span className="text-white">{limitedFundData?.purchased_count ?? 0}/{limitedFundData?.max_count ?? 3}</span>
                  </span>
                </div>
              </div>

              <button
                onClick={() => {
                  if (!canPurchaseLimited) {
                    if (!isEventActive) toast.error('限购活动已结束');
                    else toast.error('已达到限购数量');
                    return;
                  }
                  setConfirmLimitedPurchase(true);
                }}
                disabled={purchasingLimited}
                className="absolute disabled:opacity-60"
                style={{ bottom: '10.5%', left: '8%', right: '8%' }}
              >
                <img src="/images/xianding_liji.png" alt="立即购买" className="w-full" />
              </button>

              <div className="absolute flex items-center justify-center gap-2 pr-6" style={{ bottom: '3.2%', left: 0, right: 0, height: '5%' }}>
                <span className="text-[#ffecc0] text-sm font-bold tracking-wider" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                  限购倒计时{' '}剩余
                </span>
                <span className="text-white text-xl font-black tracking-widest" style={{ fontFamily: 'monospace', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                  {pad2(countdown.hours)}:{pad2(countdown.minutes)}:{pad2(countdown.seconds)}
                </span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {confirmLimitedPurchase && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl px-5 py-5 w-[320px]">
            <h3 className="text-sm font-bold text-gray-800 mb-4 text-center">选择支付方式</h3>
            <div className="space-y-3">
              <button
                onClick={() => handleLimitedPurchase('WX')}
                disabled={purchasingLimited}
                className="w-full bg-[#07c160] text-white text-sm font-bold py-3 rounded-lg disabled:opacity-60"
              >
                {purchasingLimited ? '处理中...' : '微信'}
              </button>
              <button
                onClick={() => handleLimitedPurchase('ALI')}
                disabled={purchasingLimited}
                className="w-full bg-[#1677ff] text-white text-sm font-bold py-3 rounded-lg disabled:opacity-60"
              >
                {purchasingLimited ? '处理中...' : '支付宝'}
              </button>
              <button
                onClick={() => handleLimitedPurchase('ALI2')}
                disabled={purchasingLimited}
                className="w-full bg-[#1677ff]/80 text-white text-sm font-bold py-3 rounded-lg disabled:opacity-60"
              >
                {purchasingLimited ? '处理中...' : '支付宝2'}
              </button>
            </div>
            <button
              onClick={() => setConfirmLimitedPurchase(false)}
              className="w-full mt-3 text-gray-500 text-sm py-2"
            >
              取消
            </button>
          </div>
        </div>,
        document.body
      )}

      {showCertModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl px-5 py-5 w-[340px] max-h-[75vh] flex flex-col">
            <h3 className="text-sm font-bold text-gray-800 mb-3 text-center">我的证书</h3>
            {certsLoading ? (
              <p className="text-center text-gray-400 text-xs py-8">加载中...</p>
            ) : certs.length === 0 ? (
              <p className="text-center text-gray-400 text-xs py-8">暂无购买记录</p>
            ) : (
              <div className="overflow-y-auto space-y-2 flex-1">
                {certs.map((cert) => (
                  <button
                    key={cert.id}
                    onClick={() => {
                      setShowCertModal(false);
                      navigate(`/fund-receipt?id=${cert.id}`);
                    }}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-[#c4161c] hover:bg-red-50 transition-colors"
                  >
                    <p className="text-[13px] font-bold text-gray-800">{cert.fund_info?.name ?? cert.fund_type}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[11px] text-gray-500">{cert.cert_number}</span>
                      <span className="text-[11px] text-[#c4161c] font-bold">
                        ¥{cert.fund_info ? cert.fund_info.value.toLocaleString() : '—'}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">{cert.created_at}</p>
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowCertModal(false)}
              className="w-full mt-3 text-gray-500 text-sm py-2"
            >
              关闭
            </button>
          </div>
        </div>,
        document.body
      )}

      {confirmFund && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl px-5 py-5 w-[320px]">
            <h3 className="text-sm font-bold text-gray-800 mb-4 text-center">选择支付方式</h3>
            <div className="space-y-3">
              <button
                onClick={() => handlePurchase(confirmFund, 'WX')}
                disabled={!!purchasing}
                className="w-full bg-[#07c160] text-white text-sm font-bold py-3 rounded-lg disabled:opacity-60"
              >
                {purchasing === confirmFund ? '处理中...' : '微信'}
              </button>
              <button
                onClick={() => handlePurchase(confirmFund, 'ALI')}
                disabled={!!purchasing}
                className="w-full bg-[#1677ff] text-white text-sm font-bold py-3 rounded-lg disabled:opacity-60"
              >
                {purchasing === confirmFund ? '处理中...' : '支付宝'}
              </button>
              <button
                onClick={() => handlePurchase(confirmFund, 'ALI2')}
                disabled={!!purchasing}
                className="w-full bg-[#1677ff]/80 text-white text-sm font-bold py-3 rounded-lg disabled:opacity-60"
              >
                {purchasing === confirmFund ? '处理中...' : '支付宝2'}
              </button>
            </div>
            <button
              onClick={() => setConfirmFund(null)}
              className="w-full mt-3 text-gray-500 text-sm py-2"
            >
              取消
            </button>
          </div>
        </div>,
        document.body
      )}

      {showPendingModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl px-5 py-5 w-[320px] text-center">
            <div className="text-4xl mb-3">⏳</div>
            <h3 className="text-sm font-bold text-gray-800 mb-2">支付确认中</h3>
            <p className="text-xs text-gray-500 mb-4">
              如果您已完成支付，请稍等片刻，系统将自动确认。
            </p>
            <button
              onClick={() => setShowPendingModal(false)}
              className="bg-[#c4161c] text-white text-sm px-6 py-2 rounded-lg"
            >
              我知道了
            </button>
          </div>
        </div>,
        document.body
      )}

      {(purchasing || purchasingLimited) && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl px-8 py-6 flex flex-col items-center gap-3 shadow-xl">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#c4161c] rounded-full animate-spin" />
            <span className="text-sm font-medium text-gray-700">正在创建订单...</span>
          </div>
        </div>,
        document.body
      )}

      <CardFundPromotionModal visible={showFundPromoModal} onClose={() => setShowFundPromoModal(false)} />
      <CardFundPromotionModalV2 visible={showFundPromoModal} onClose={() => setShowFundPromoModal(false)} />
      <CardFundPromotionModalV3 visible={showFundPromoModal} onClose={() => setShowFundPromoModal(false)} />
    </div>
  );
}
